import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowRight, Building2, Package, Database, Calculator, ShieldCheck, BadgeCheck,
  Minus, Plus, Fingerprint, Lock, QrCode, History, ClipboardCheck, CheckCircle2, Factory, Layers,
} from "lucide-react";

const GREEN = "#0F4D32";
const NAVY = "#0F172A";
const MAILTO = "mailto:info@epd-nigeria.com?subject=EPD%20Nigeria%20enquiry";

const openContact = () => window.dispatchEvent(new CustomEvent("epd-open-contact"));

function ContactDialog() {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [f, setF] = useState({ name: "", company: "", work_email: "", phone: "", message: "" });
  useEffect(() => {
    const h = () => { setSent(false); setOpen(true); };
    window.addEventListener("epd-open-contact", h);
    return () => window.removeEventListener("epd-open-contact", h);
  }, []);
  const upd = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.company.trim() || !f.work_email.trim() || !f.message.trim()) {
      toast.error("Please complete name, company, work email and message.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.work_email.trim())) {
      toast.error("Please enter a valid work email.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", { ...f, work_email: f.work_email.trim() });
      setSent(true);
      toast.success("Enquiry sent. Our team will reply shortly.");
    } catch (err) {
      toast.error(apiError(err) || "Could not send your enquiry. Please email info@epd-nigeria.com.");
    } finally {
      setSending(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="contact-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Talk to our team</DialogTitle>
          <DialogDescription>Tell us about your organisation and we'll be in touch.</DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="py-6 text-center" data-testid="contact-success">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-600" />
            <p className="mt-3 font-medium text-slate-900">Thank you — your enquiry has been sent.</p>
            <p className="text-sm text-slate-500 mt-1">Our team at EPD Nigeria will reply to your work email shortly.</p>
            <Button className="mt-5" onClick={() => setOpen(false)} data-testid="contact-close">Close</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div><Label>Name</Label><Input value={f.name} onChange={upd("name")} data-testid="contact-name" required /></div>
            <div><Label>Company</Label><Input value={f.company} onChange={upd("company")} data-testid="contact-company" required /></div>
            <div><Label>Work email</Label><Input type="email" value={f.work_email} onChange={upd("work_email")} data-testid="contact-email" required /></div>
            <div><Label>Phone <span className="text-slate-400">(optional)</span></Label><Input value={f.phone} onChange={upd("phone")} data-testid="contact-phone" /></div>
            <div><Label>Message</Label><Textarea rows={4} value={f.message} onChange={upd("message")} data-testid="contact-message" required /></div>
            <Button type="submit" className="w-full" disabled={sending} data-testid="contact-submit">
              {sending ? "Sending…" : "Send enquiry"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Logo({ light = false, size = 32 }) {
  const box = light ? "#FFFFFF" : NAVY;
  const node = "#10B981";
  return (
    <div className="flex items-center gap-2.5">
      <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden>
        <rect x="1" y="1" width="30" height="30" rx="6" fill={box} />
        <circle cx="10" cy="10" r="2.6" fill={node} />
        <circle cx="22" cy="10" r="2.6" fill="#64748B" />
        <circle cx="16" cy="22" r="2.6" fill="#E2E8F0" />
        <path d="M10 10 L22 10 L16 22 Z" stroke={node} strokeWidth="1.4" opacity="0.75" fill="none" />
      </svg>
      <span className={`font-display font-semibold tracking-tight ${light ? "text-white" : "text-slate-900"}`}>
        <span style={{ color: light ? "#fff" : GREEN }}>EPD</span> Nigeria
      </span>
    </div>
  );
}

const NAV_LINKS = [["Platform", "#platform"], ["How it works", "#workflow"], ["Standards", "#standards"], ["Pricing", "#pricing"]];

function Nav() {
  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/get-started"><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map(([l, h]) => <a key={l} href={h} className="text-sm text-slate-600 hover:text-slate-900 transition-colors">{l}</a>)}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="nav-signin-button" className="text-sm font-medium text-slate-600 hover:text-slate-900">Sign in</Link>
          <Link to="/register?plan=Annual"><Button size="sm" data-testid="nav-start-epd-button" style={{ backgroundColor: GREEN }} className="text-white hover:opacity-90">Start your EPD</Button></Link>
        </div>
      </div>
    </header>
  );
}

function HeroVisual() {
  const mods = [["A1", "Raw materials", "252.1"], ["A2", "Transport", "11.0"], ["A3", "Manufacturing", "25.3"]];
  return (
    <div className="relative">
      <div className="absolute -inset-3 rounded-2xl bg-emerald-50/60 -z-10" />
      <div className="rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200" style={{ backgroundColor: NAVY }}>
          <div className="text-white text-sm font-medium">EPD Project · Ready-Mix (illustrative)</div>
          <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">TECHNICAL REVIEW COMPLETE</span>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">GWP-total · A1–A3</div>
              <div className="font-mono text-3xl font-semibold text-slate-900">288.4 <span className="text-base text-slate-400">kg CO₂e/m³</span></div>
            </div>
            <div className="text-right">
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Data quality</div>
              <div className="font-mono text-2xl font-semibold" style={{ color: GREEN }}>96%</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {mods.map(([m, l, v]) => (
              <div key={m} className="rounded-lg border border-slate-200 p-3">
                <div className="font-mono text-xs font-semibold" style={{ color: GREEN }}>{m}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">{l}</div>
                <div className="font-mono text-sm text-slate-900 mt-1">{v}</div>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {[["Dataset provenance frozen", true], ["Automated technical validation passed", true], ["Independent verification (external) — pending", false]].map(([t, ok]) => (
              <div key={t} className="flex items-center gap-2 text-sm">
                {ok ? <CheckCircle2 className="h-4 w-4" style={{ color: GREEN }} /> : <ShieldCheck className="h-4 w-4 text-slate-300" />}
                <span className={ok ? "text-slate-700" : "text-slate-400"}>{t}</span>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-slate-400 text-right">Illustrative platform view</div>
        </div>
      </div>
    </div>
  );
}

const CAPS = [
  [Database, "Collect", "Product, plant and production data."],
  [Calculator, "Calculate", "Deterministic LCA with traceable datasets."],
  [ClipboardCheck, "Validate", "Automated technical and data-quality checks."],
  [BadgeCheck, "Publish", "Verification workflow, declaration ID and QR validation."],
];
const STEPS = [[Building2, "01", "Organisation"], [Package, "02", "Product"], [Database, "03", "Production Data"], [Calculator, "04", "Calculate"], [ShieldCheck, "05", "Verify"], [BadgeCheck, "06", "Publish"]];
const CONTROLS = [[Lock, "Role-based access"], [Building2, "Multi-plant workspace"], [ClipboardCheck, "Evidence records"], [Fingerprint, "Dataset provenance"], [History, "Version control"], [BadgeCheck, "Immutable issued declarations"]];

function Pricing() {
  const nav = useNavigate();
  const [users, setUsers] = useState(1);
  const fmt = (n) => "₦" + n.toLocaleString("en-NG");
  return (
    <section id="pricing" className="max-w-7xl mx-auto px-6 py-20 lg:py-24 scroll-mt-20">
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Enterprise access</div>
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mt-3 text-slate-900">Enterprise access</h2>
      <div className="mt-8 flex items-center gap-4">
        <span className="text-sm text-slate-600">Named users</span>
        <div className="flex items-center gap-2 rounded-lg border border-slate-300 p-1">
          <button data-testid="seat-decrement" onClick={() => setUsers((u) => Math.max(1, u - 1))} className="h-8 w-8 rounded flex items-center justify-center hover:bg-slate-100"><Minus className="h-4 w-4" /></button>
          <input data-testid="user-seat-input" type="number" min={1} value={users} onChange={(e) => setUsers(Math.max(1, parseInt(e.target.value) || 1))} className="w-16 text-center font-mono text-lg font-semibold outline-none" />
          <button data-testid="seat-increment" onClick={() => setUsers((u) => u + 1)} className="h-8 w-8 rounded flex items-center justify-center hover:bg-slate-100"><Plus className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-6 mt-8 max-w-3xl">
        <div data-testid="pricing-monthly-card" className="rounded-xl border border-slate-200 bg-white p-7">
          <div className="text-sm font-medium text-slate-500">Enterprise Monthly</div>
          <div className="mt-2 font-mono text-2xl font-semibold text-slate-900">₦2,500,000<span className="text-sm text-slate-400 font-sans"> / month / named user</span></div>
          <div className="mt-4 text-sm text-slate-600">{users} user{users > 1 ? "s" : ""}: <span className="font-mono font-semibold text-slate-900" data-testid="monthly-total">{fmt(2500000 * users)}/mo</span></div>
          <Button data-testid="pricing-select-monthly-button" onClick={() => nav(`/register?plan=Monthly&users=${users}`)} variant="outline" className="w-full mt-6">Start Registration</Button>
        </div>
        <div data-testid="pricing-annual-card" className="rounded-xl border-2 bg-white p-7 relative" style={{ borderColor: GREEN }}>
          <span className="absolute -top-3 left-7 text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded text-white" style={{ backgroundColor: GREEN }}>Recommended</span>
          <div className="text-sm font-medium text-slate-500">Enterprise Annual</div>
          <div className="mt-2 font-mono text-2xl font-semibold text-slate-900">₦25,000,000<span className="text-sm text-slate-400 font-sans"> / year / named user</span></div>
          <div className="mt-1 text-xs font-medium" style={{ color: GREEN }}>Save ₦5,000,000 annually</div>
          <div className="mt-3 text-sm text-slate-600">{users} user{users > 1 ? "s" : ""}: <span className="font-mono font-semibold text-slate-900" data-testid="annual-total">{fmt(25000000 * users)}/yr</span></div>
          <Button data-testid="pricing-select-annual-button" onClick={() => nav(`/register?plan=Annual&users=${users}`)} style={{ backgroundColor: GREEN }} className="w-full mt-4 text-white hover:opacity-90">Start Registration</Button>
        </div>
      </div>
      <p className="mt-6 text-sm text-slate-600">Multi-user or multi-plant deployment? <button type="button" onClick={openContact} className="font-medium underline" style={{ color: GREEN }} data-testid="pricing-talk-cta">Talk to our team.</button></p>
      <p className="mt-2 text-xs text-slate-500">Independent verification and applicable third-party programme registration fees are separate.</p>
    </section>
  );
}

export default function GetStarted() {
  return (
    <div className="min-h-screen bg-[#FAFAFA] antialiased">
      <ContactDialog />
      <Nav />

      {/* HERO */}
      <section id="platform" className="max-w-7xl mx-auto px-6 pt-14 lg:pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Environmental Product Declarations for Manufacturers</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mt-5 text-slate-900">From plant data to credible Environmental Product Declarations.</h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">Create traceable, standards-aligned EPDs through one controlled workflow for data, LCA calculation, technical review and independent verification.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/register?plan=Annual"><Button size="lg" data-testid="hero-start-epd-cta" style={{ backgroundColor: GREEN }} className="text-white hover:opacity-90">Start your EPD <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
              <Button size="lg" variant="outline" data-testid="hero-talk-cta" onClick={openContact}>Talk to our team</Button>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2">
              {["Standards-aligned", "Traceable calculations", "Independent verification workflow"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="h-4 w-4" style={{ color: GREEN }} />{t}</div>
              ))}
            </div>
          </div>
          <HeroVisual />
        </div>
      </section>

      {/* STANDARDS */}
      <section id="standards" className="border-y border-slate-200 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 py-7 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-10">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 shrink-0">Built around internationally recognised EPD frameworks</div>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
            {["ISO 14025", "ISO 21930", "EN 15804+A2"].map((s) => <span key={s} className="font-display text-lg font-semibold text-slate-800">{s}</span>)}
          </div>
          <p className="text-xs text-slate-500 sm:ml-auto sm:max-w-[16rem]">Applicable PCR and programme requirements are configured per project.</p>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="value" className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 max-w-2xl">Everything you need to move from data to declaration</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {CAPS.map(([Icon, t, b]) => (
            <div key={t} className="rounded-xl border border-slate-200 bg-white p-6 hover:shadow-sm transition-all">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#E8F4EE" }}><Icon className="h-5 w-5" style={{ color: GREEN }} /></div>
              <h3 className="font-display text-lg font-semibold tracking-tight mt-4 text-slate-900">{t}</h3>
              <p className="text-sm text-slate-600 mt-1.5">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WORKFLOW */}
      <section id="workflow" className="bg-white border-y border-slate-200 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">One controlled EPD workflow</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-9">
            {STEPS.map(([Icon, n, t], i) => (
              <div key={n} data-testid={`workflow-step-${i + 1}`} className="rounded-xl border border-slate-200 p-4 bg-[#FAFAFA]">
                <div className="flex items-center justify-between"><span className="font-mono text-sm font-semibold" style={{ color: GREEN }}>{n}</span><Icon className="h-4 w-4 text-slate-400" /></div>
                <div className="font-display text-sm font-medium text-slate-900 mt-2 leading-tight">{t}</div>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-600 mt-6">From primary manufacturing data to a verifier-ready declaration, with traceability at every step.</p>
          <p className="text-xs text-slate-500 mt-1">Independent verification is performed externally.</p>
        </div>
      </section>

      {/* PRODUCT / READINESS */}
      <section id="showcase" className="max-w-7xl mx-auto px-6 py-20 lg:py-24 scroll-mt-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">See exactly where your EPD stands.</h2>
            <div className="mt-6 space-y-3">
              {["Trace every result to its source", "Identify gaps before verification", "Maintain a complete audit trail"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="h-5 w-5 shrink-0" style={{ color: GREEN }} />{t}</div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden">
            <div className="flex" >
              {["CALCULABLE", "VERIFIER_READY", "ISSUABLE"].map((s, i) => (
                <div key={s} className={`flex-1 text-center py-3 font-mono text-[11px] tracking-wide ${i < 2 ? "text-white" : "text-slate-400 bg-slate-50"}`} style={i < 2 ? { backgroundColor: i === 0 ? GREEN : NAVY } : {}}>{s}</div>
              ))}
            </div>
            <div className="p-5 space-y-2.5">
              {[["Data completeness", "96%"], ["Evidence", "attached"], ["Dataset provenance", "frozen"], ["Calculation", "deterministic"], ["Technical validation", "passed"], ["Verification", "external — pending"]].map(([t, v], i) => (
                <div key={t} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700">{t}</span>
                  <span className={`font-mono text-xs ${i === 5 ? "text-slate-400" : ""}`} style={i !== 5 ? { color: GREEN } : {}}>{v}</span>
                </div>
              ))}
              <div className="text-[10px] text-slate-400 text-right pt-1">Illustrative platform view</div>
            </div>
          </div>
        </div>
      </section>

      {/* MANUFACTURERS */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">Built initially for construction-material manufacturers</h2>
          <div className="grid sm:grid-cols-2 gap-6 mt-9 max-w-3xl">
            {[[Factory, "Cement", "EPDs for cement and clinker-based binder products."], [Layers, "Ready-Mix Concrete", "EPDs per m³ across strength classes and mix designs."]].map(([Icon, t, b]) => (
              <div key={t} className="rounded-xl border border-slate-200 p-7 bg-[#FAFAFA]">
                <Icon className="h-7 w-7" style={{ color: GREEN }} />
                <h3 className="font-display text-xl font-semibold tracking-tight mt-4 text-slate-900">{t}</h3>
                <p className="text-sm text-slate-600 mt-1.5">{b}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4">Additional construction-product categories will follow.</p>
        </div>
      </section>

      {/* ENTERPRISE CONTROL */}
      <section className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">Built for enterprise control</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-9">
          {CONTROLS.map(([Icon, t]) => (
            <div key={t} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-4">
              <Icon className="h-5 w-5 shrink-0" style={{ color: GREEN }} /><span className="text-sm font-medium text-slate-800">{t}</span>
            </div>
          ))}
        </div>
      </section>

      <Pricing />

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="rounded-2xl p-10 lg:p-16 text-center" style={{ backgroundColor: NAVY }}>
          <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight text-white">Ready to start your EPD?</h2>
          <p className="mt-3 text-slate-300 max-w-xl mx-auto">Bring your product and plant data into one controlled, traceable workflow.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/register?plan=Annual"><Button size="lg" style={{ backgroundColor: GREEN }} className="text-white hover:opacity-90" data-testid="final-start-epd-cta">Start your EPD <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
            <Button size="lg" variant="outline" className="bg-transparent text-white border-slate-600 hover:bg-white/10 hover:text-white" data-testid="final-talk-cta" onClick={openContact}>Talk to our team</Button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <Logo />
            <p className="text-sm text-slate-500 mt-2">Enterprise Environmental Product Declaration Infrastructure.</p>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <a href="#platform" className="text-slate-500 hover:text-slate-900">Platform</a>
            <a href="#workflow" className="text-slate-500 hover:text-slate-900">How it works</a>
            <a href="#standards" className="text-slate-500 hover:text-slate-900">Standards</a>
            <a href="#pricing" className="text-slate-500 hover:text-slate-900">Pricing</a>
            <a href={MAILTO} className="font-medium" style={{ color: GREEN }}>info@epd-nigeria.com</a>
            <Link to="/privacy" className="text-slate-500 hover:text-slate-900" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/terms" className="text-slate-500 hover:text-slate-900" data-testid="footer-terms-link">Terms</Link>
          </div>
        </div>
        <p className="text-xs text-slate-500 max-w-7xl mx-auto px-6 pb-8 border-t border-slate-100 pt-6">Independent verification and programme registration are performed by applicable third parties.</p>
      </footer>
    </div>
  );
}
